import React, { useState, useEffect } from "react";
import { Button, Input, Row, Col, message, Tag, Modal } from "antd";
import {
  ScanOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import TableContainer from "../../components/TableContainer";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { scanBarcode, closeHubReconciliation } from "../../actionsMethods/hubReconciliationMethods";
import { APIs, getToken, getPremiseId } from "../../utils/worker";
import { isValidTrackingAwb } from "../../utils/trackingValidation";
import { getApiMessage } from "./apiMessage";
import { STATUS_TAG } from "./hubReconciliationConstants";
import "./styles.scss";

const StartReconciliation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const reconcileId = location.state?.reconcileId || searchParams.get("reconcileId");
  const [barcode, setBarcode] = useState("");
  const [scannedData, setScannedData] = useState([]);
  const [currentBarcode, setCurrentBarcode] = useState("");
  const [counts, setCounts] = useState({
    availableCount: 0,
    overageCount: 0,
    excessInscanCount: 0,
    totalCount: 0,
  });

  const {
    result: scanResponseData,
    error: scanError,
  } = useSelector((state) => state.hubReconciliationReducer.scanBarcode);

  const {
    result: closeResponseData,
    error: closeError,
  } = useSelector((state) => state.hubReconciliationReducer.closeHubReconciliation);

  const updateLocalCounts = (items) => {
    const availableCount = items.filter((i) => i.status === "AVAILABLE").length;
    const overageCount = items.filter((i) => i.status === "OVERAGE").length;
    const excessInscanCount = items.filter((i) => i.status === "EXCESS_INSCAN").length;
    setCounts({
      availableCount,
      overageCount,
      excessInscanCount,
      totalCount: items.length,
    });
  };

  const fetchInProgressShipments = async () => {
    try {
      const tokenStr = getToken();
      const token = tokenStr?.startsWith("Bearer ") ? tokenStr.split("Bearer ")[1] : tokenStr;
      const premiseIdStr = getPremiseId();
      const premiseId = premiseIdStr?.startsWith("Bearer ") ? premiseIdStr.split("Bearer ")[1] : premiseIdStr;
      const userInfoData = JSON.parse(localStorage.getItem("userId") || "{}");
      const userId = userInfoData?.userInfo?.userId || "";

      const response = await axios.get(
        `${APIs.baseURL}/hub-reconciliation-service/v1/hub-reconcile/shipments/${reconcileId}?type=IN_PROGRESS`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "SMCS-PREMISE-ID": premiseId,
            "USER-ID": userId,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        const responseData = response.data?.data || response.data || {};
        let items = [];
        if (Array.isArray(responseData?.shipments)) {
          items = responseData.shipments;
        } else if (Array.isArray(responseData)) {
          items = responseData;
        } else if (Array.isArray(responseData?.data)) {
          items = responseData.data;
        }

        const mappedData = items.reverse().map((item, index) => ({
          id: index + 1,
          barcode: item.awbNumber || item.barcode || "N/A",
          time: item.scannedTime || item.scannedAt || item.createdAt || "",
          status: item.status || "AVAILABLE",
        }));

        setScannedData(mappedData);
        if (responseData?.availableCount != null) {
          setCounts({
            availableCount: responseData.availableCount ?? 0,
            overageCount: responseData.overageCount ?? 0,
            excessInscanCount: responseData.excessInscanCount ?? 0,
            totalCount: responseData.totalCount ?? mappedData.length,
          });
        } else {
          updateLocalCounts(mappedData);
        }
      }
    } catch (error) {
      console.error("Error fetching in-progress shipments", error);
      message.error(
        getApiMessage(error?.response?.data || error, "Failed to load scanned shipments"),
        3
      );
    }
  };

  useEffect(() => {
    if (reconcileId) {
      fetchInProgressShipments();
    }
  }, [reconcileId]);

  const handleScan = () => {
    if (barcode.trim()) {
      const scannedCode = barcode.trim();

      if (!isValidTrackingAwb(scannedCode)) {
        message.error("Invalid AWB number format", 1);
        setBarcode("");
        return;
      }

      const isDuplicate = scannedData.some((item) => item.barcode === scannedCode);
      if (isDuplicate) {
        message.warning(`Barcode ${scannedCode} has already been scanned`, 1);
        setBarcode("");
        return;
      }

      setCurrentBarcode(scannedCode);

      const tokenStr = getToken();
      const token = tokenStr?.startsWith("Bearer ") ? tokenStr.split("Bearer ")[1] : tokenStr;

      const premiseIdStr = getPremiseId();
      const premiseId = premiseIdStr?.startsWith("Bearer ") ? premiseIdStr.split("Bearer ")[1] : premiseIdStr;

      const userInfoData = JSON.parse(localStorage.getItem("userId") || "{}");
      const userId = userInfoData?.userInfo?.userId || "";

      dispatch(
        scanBarcode({
          reconcileId,
          awbNumber: scannedCode,
          token,
          premiseId,
          userId,
        })
      );

      setBarcode("");
    }
  };

  useEffect(() => {
    if (scanResponseData && currentBarcode) {
      const results = Array.isArray(scanResponseData?.data)
        ? scanResponseData.data
        : scanResponseData?.data
          ? [scanResponseData.data]
          : [];

      setScannedData((prev) => {
        let next = [...prev];

        // For bag scans, backend can return multiple shipment AWBs. Always display returned AWBs (not the bag code).
        for (const res of results) {
          const awb = (res?.awbNumber || res?.barcode || "").toString().trim();
          if (!awb) continue;
          if (next.some((item) => item.barcode === awb)) continue;

          next.unshift({
            barcode: awb,
            time: new Date().toLocaleTimeString(),
            status: res?.status || "AVAILABLE",
          });
        }

        // If backend returned nothing usable, avoid showing the scanned bag code as a shipment row.
        if (next.length === prev.length) {
          return prev;
        }

        const withIds = next.map((item, index) => ({ ...item, id: index + 1 }));
        updateLocalCounts(withIds);
        return withIds;
      });
      message.success(getApiMessage(scanResponseData, "Shipment scanned successfully"), 2);
      setCurrentBarcode("");
    } else if (scanError && currentBarcode) {
      message.error(getApiMessage(scanError, "Failed to scan barcode"), 3);
      setCurrentBarcode("");
    }
  }, [scanError, scanResponseData]);

  const handleCloseReconciliation = () => {
    if (!reconcileId) {
      navigate(-1);
      return;
    }

    Modal.confirm({
      title: "Close reconciliation?",
      icon: <ExclamationCircleOutlined />,
      content:
        "Unscanned shipments will be marked as Missing. This cannot be undone.",
      okText: "Close Reconciliation",
      okType: "danger",
      cancelText: "Keep Scanning",
      onOk: () => {
        const tokenStr = getToken();
        const token = tokenStr?.startsWith("Bearer ") ? tokenStr.split("Bearer ")[1] : tokenStr;

        const premiseIdStr = getPremiseId();
        const premiseId = premiseIdStr?.startsWith("Bearer ")
          ? premiseIdStr.split("Bearer ")[1]
          : premiseIdStr;

        const userInfoData = JSON.parse(localStorage.getItem("userId") || "{}");
        const userId = userInfoData?.userInfo?.userId || "";

        dispatch(
          closeHubReconciliation({
            reconcileId,
            token,
            premiseId,
            userId,
          })
        );
      },
    });
  };

  useEffect(() => {
    if (closeResponseData) {
      message.success(getApiMessage(closeResponseData, "Hub reconciliation closed successfully"), 2);
      navigate("/hub-reconciliation");
    } else if (closeError) {
      message.error(getApiMessage(closeError, "Failed to close Hub Reconciliation"), 3);
    }
  }, [closeResponseData, closeError]);

  const columns = [
    { title: "S.No", dataIndex: "id", key: "id", width: 80 },
    { title: "Barcode Number", dataIndex: "barcode", key: "barcode" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const cfg = STATUS_TAG[status] || { color: "default", label: status };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
  ];

  return (
    <div className="dashboard-container hub-recon" style={{ padding: "20px" }}>
      <div className="hub-recon__header">
        <div>
          <div className="hub-recon__title">
            <ArrowLeftOutlined
              className="hub-recon__back"
              onClick={() => navigate("/hub-reconciliation")}
            />
            Start Reconciliation
          </div>
          <div className="hub-recon__subtitle">
            Reconcile ID: <strong>{reconcileId || "—"}</strong>
            {" · "}
            Scan AWB / bag barcodes, then close when done
          </div>
        </div>
        <Button
          onClick={handleCloseReconciliation}
          className="hub-recon__btn hub-recon__btn--danger"
          type="primary"
          danger
          icon={<CloseCircleOutlined />}
        >
          Close Reconciliation
        </Button>
      </div>

      <div className="master-main-container view-all-staff">
        <div className="hub-recon__panel">
          <div className="hub-recon__section-label">
            <ScanOutlined style={{ marginRight: 6 }} />
            Scan barcode
          </div>
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} md={14} lg={12}>
              <div className="filter-label" style={{ marginBottom: 8, fontWeight: 500 }}>
                Shipment / Bag Number
              </div>
              <Input
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder="Scan or type barcode, then press Enter"
                prefix={<ScanOutlined style={{ color: "#9ca3af" }} />}
                bordered
                style={{ borderRadius: "4px", height: "35px", fontSize: 14 }}
                onKeyPress={(e) => {
                  const forbiddenChars = /[@!$#%^&*=+()""';?.><]/;
                  if (forbiddenChars.test(e.key)) {
                    e.preventDefault();
                    message.destroy();
                  }
                }}
                onPressEnter={handleScan}
                autoFocus
              />
              <div className="hub-recon__helper">
                Duplicates are blocked automatically. Latest scan appears at the top.
              </div>
            </Col>
          </Row>

          <div className="hub-recon__stats hub-recon__stats--scan">
            <div className="hub-recon__stat available" style={{ cursor: "default" }}>
              <div className="hub-recon__stat-label">Available</div>
              <div className="hub-recon__stat-value">{counts.availableCount}</div>
              <div className="hub-recon__stat-hint">Matched shipments</div>
            </div>
            <div className="hub-recon__stat overage" style={{ cursor: "default" }}>
              <div className="hub-recon__stat-label">Overage</div>
              <div className="hub-recon__stat-value">{counts.overageCount}</div>
              <div className="hub-recon__stat-hint">Extra scans</div>
            </div>
            <div className="hub-recon__stat excess-inscan" style={{ cursor: "default" }}>
              <div className="hub-recon__stat-label">Excess Inscan</div>
              <div className="hub-recon__stat-value">{counts.excessInscanCount}</div>
              <div className="hub-recon__stat-hint">Not in the system</div>
            </div>
            <div className="hub-recon__stat total is-active" style={{ cursor: "default" }}>
              <div className="hub-recon__stat-label">Scanned Total</div>
              <div className="hub-recon__stat-value">{counts.totalCount}</div>
              <div className="hub-recon__stat-hint">All scanned so far</div>
            </div>
          </div>
        </div>

        <div className="hub-recon__table-wrap">
          <TableContainer
            className="table-head-sticky"
            dataSource={scannedData}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} scanned`,
            }}
            scroll={{ x: "max-content", y: "calc(100vh - 420px)" }}
          />
        </div>
      </div>
    </div>
  );
};

export default StartReconciliation;
